import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { AppDispatch } from "../store/store";
import { subscribeNewsletter } from "../store/NewsletterSlice";

interface NewsletterFormProps {
  className?: string;
  inputClassName?: string;
  buttonClassName?: string;
}

export default function NewsletterForm({
  className = "",
  inputClassName = "",
  buttonClassName = "",
}: NewsletterFormProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error(t("footer.enterEmail", "Please enter an email address"));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      toast.error(t("footer.invalidEmail", "Please enter a valid email address"));
      return;
    }

    setIsSubmitting(true);
    try {
      const result: any = await dispatch(subscribeNewsletter(trimmed));
      if (subscribeNewsletter.fulfilled.match(result)) {
        if (result.payload?.data?.alreadySubscribed) {
          toast.info(t("footer.alreadySubscribed", "You are already subscribed to our newsletter."));
        } else {
          toast.success(t("footer.subscribeSuccess", "Thank you for subscribing to our newsletter!"));
        }
        setEmail("");
      } else {
        toast.error(result.payload || t("footer.subscribeError", "Subscription failed. Please try again."));
      }
    } catch (err) {
      toast.error(t("footer.subscribeError", "Subscription failed. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubscribe} className={`flex items-center ${className}`}>
      <div className="relative flex-1">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("footer.emailPlaceholder", "Your email")}
          disabled={isSubmitting}
          style={{ color: "#ffffff", backgroundColor: "#7D6146" }}
          className={`py-3 px-4 w-56 sm:w-64 md:w-80 rounded-l-md text-white placeholder-gray-200 border border-white/20 focus:border-white/50 focus:outline-none text-sm md:text-base font-medium transition-all ${inputClassName}`}
        />
      </div>
      <button
        type="submit"
        disabled={isSubmitting}
        className={`bg-white text-[#4B2B09] hover:bg-gray-100 font-bold py-3 px-5 rounded-r-md transition-colors duration-200 text-sm md:text-base whitespace-nowrap disabled:opacity-60 cursor-pointer shadow-sm ${buttonClassName}`}
      >
        {isSubmitting ? (
          <span className="flex items-center gap-1">
            <svg
              className="animate-spin h-4 w-4 text-[#4B2B09]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>{t("footer.submitting", "...")}</span>
          </span>
        ) : (
          t("footer.submit", "Submit")
        )}
      </button>
    </form>
  );
}
